/**
 * OpenAI page_viewed lifecycle — synchronous, SDK-free.
 *
 * Root calls these synchronously at navigation / consent-change time so the
 * consent state is captured when the event happens, not when a lazy import
 * resolves. The external OpenAI SDK stays lazy in `openaiAdsPixel.ts`.
 *
 * - Only sanitized page ids are stored (no query, hash or token segments).
 * - Re-recording the current page is a no-op; A -> B -> A is three views.
 * - Views recorded under granted consent are kept (max 10, 10 min).
 * - Views recorded while denied are never queued; a late grant may send
 *   only the current view.
 * - Every observed revocation drops all pending views and bumps an epoch so
 *   the SDK consent state is re-synced before anything is released.
 */
import { hasAdsConsent } from "@/lib/cookie-consent";

const PENDING_MAX = 10;
const PENDING_TTL_MS = 10 * 60 * 1000;

const COLLAPSED_PREFIX_RE =
  /^(?:\/(?:ar|en|fr))?\/(?:order|a|l|driver|account|thank-you|track-order)(?=\/|$)/i;

/** Nonpersonal page id: pathname only, token/code routes collapsed. */
export function openAiPageId(rawPath: string): string {
  let pathname = "/";
  try {
    pathname = new URL(rawPath || "/", "https://page.invalid").pathname || "/";
  } catch {
    return "/";
  }
  const m = pathname.match(COLLAPSED_PREFIX_RE);
  return m ? m[0].toLowerCase() : pathname;
}

type View = { seq: number; id: string; at: number; consented: boolean };
let viewSeq = 0;
let currentView: View | null = null;
let pending: View[] = [];
let sentSeq = -1;
let consentEpoch = 0;

/** Record a navigation synchronously, capturing consent at view time. */
export function recordOpenAiPageView(rawPath: string): void {
  if (typeof window === "undefined") return;
  const id = openAiPageId(rawPath);
  const granted = hasAdsConsent();
  if (!granted) {
    pending = [];
    consentEpoch++;
  }
  if (currentView && currentView.id === id) return;
  currentView = { seq: ++viewSeq, id, at: Date.now(), consented: granted };
  if (granted) {
    pending.push(currentView);
    if (pending.length > PENDING_MAX) pending = pending.slice(-PENDING_MAX);
  }
}

/** Call synchronously on every consent change. Revocation drops pending. */
export function noteOpenAiConsent(): void {
  if (typeof window === "undefined") return;
  if (!hasAdsConsent()) {
    pending = [];
    consentEpoch++;
  }
}

export function openAiConsentEpoch(): number {
  return consentEpoch;
}

/**
 * Deliver pending views (then the current view for a late grant) via `send`.
 * Entries are removed only after `send` returns true; on failure the rest
 * stay queued for a later flush.
 */
export function drainOpenAiPageViews(send: (id: string) => boolean): void {
  if (!hasAdsConsent()) {
    pending = [];
    return;
  }
  const cutoff = Date.now() - PENDING_TTL_MS;
  pending = pending.filter((v) => v.at >= cutoff && v.seq > sentSeq);
  while (pending.length) {
    const v = pending[0];
    if (!send(v.id)) return;
    sentSeq = v.seq;
    pending.shift();
  }
  if (currentView && currentView.seq > sentSeq) {
    if (send(currentView.id)) sentSeq = currentView.seq;
  }
}
